import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";

const CATEGORY_SLUG_TO_GENRE: Record<string, string> = {
  strategija: "Strategy",
  seimos: "Family",
  vakareliu: "Party",
  teminiai: "Thematic",
  abstraktus: "Abstract",
  karo: "War",
  ekonominiai: "Economic",
  nuotykiu: "Adventure",
  galvosukiai: "Puzzle",
  viktorinos: "Trivia",
  kortu: "Card Game",
  vikrumo: "Dexterity",
  edukaciniai: "Educational",
  siaubo: "Horror",
  "moksline-fantastika": "Sci-Fi",
  fantastika: "Fantasy",
  istoriniai: "Historical",
  civilizaciju: "Civilization",
};

function CategoryLandingRedirect() {
  const { slug } = useParams<{ slug: string }>();
  const genre = slug ? CATEGORY_SLUG_TO_GENRE[slug] : undefined;
  if (!genre) return <Navigate replace to="/listings" />;
  return <Navigate replace to={`/listings?genres=${encodeURIComponent(genre)}`} />;
}

function SearchRedirect() {
  const search = window.location.search;
  return <Navigate replace to={`/listings${search}`} />;
}
import { AuthProvider } from "@/hooks/useAuth";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AdminRoute } from "@/components/auth/AdminRoute";
import { AppLayout } from "@/components/layout/AppLayout";
import { HomePage } from "@/pages/HomePage";
import { LoginPage } from "@/pages/LoginPage";
import { RegisterPage } from "@/pages/RegisterPage";
import { AuthCallbackPage } from "@/pages/AuthCallbackPage";
import { GamesPage } from "@/pages/GamesPage";
import { GameDetailPage } from "@/pages/GameDetailPage";
import { GamePriceHistoryPage } from "@/pages/GamePriceHistoryPage";
import { ListingsPage } from "@/pages/ListingsPage";
import { ListingDetailPage } from "@/pages/ListingDetailPage";
import { ListingGroupPage } from "@/pages/ListingGroupPage";
import { CollectionPage } from "@/pages/CollectionPage";
import { NewListingPage } from "@/pages/NewListingPage";
import { EditListingPage } from "@/pages/EditListingPage";
import { MyListingsPage } from "@/pages/MyListingsPage";
import { MyCollectionsPage } from "@/pages/MyCollectionsPage";
import { MessagesPage } from "@/pages/MessagesPage";
import { MessageThreadPage } from "@/pages/MessageThreadPage";
import { NotificationsPage } from "@/pages/NotificationsPage";
import { WishlistsPage } from "@/pages/WishlistsPage";
import { UserProfilePage } from "@/pages/UserProfilePage";
import { EditProfilePage } from "@/pages/EditProfilePage";
import { ChangeEmailPage } from "@/pages/ChangeEmailPage";
import { ChangePasswordPage } from "@/pages/ChangePasswordPage";
import { NotificationSettingsPage } from "@/pages/NotificationSettingsPage";
import { AdminAiRequestsPage } from "@/pages/AdminAiRequestsPage";
import { AdminPage } from "@/pages/AdminPage";
import { AdminUsersPage } from "@/pages/AdminUsersPage";
import { AdminErrorLogsPage } from "@/pages/AdminErrorLogsPage";
import { AdminJobsPage } from "@/pages/AdminJobsPage";
import { AdminComponentsPage } from "@/pages/AdminComponentsPage";
import { AdminListingsPage } from "@/pages/AdminListingsPage";
import { AdminShopListingsPage } from "@/pages/AdminShopListingsPage";
import { AdminGamesPage } from "@/pages/AdminGamesPage";
import { AdminGamePreviewPage } from "@/pages/AdminGamePreviewPage";
import { AdminMergeGamesPage } from "@/pages/AdminMergeGamesPage";
import { AdminOgPreviewPage } from "@/pages/AdminOgPreviewPage";
import { ForgotPasswordPage } from "@/pages/ForgotPasswordPage";
import { ResetPasswordPage } from "@/pages/ResetPasswordPage";
import { ConfirmEmailPage } from "@/pages/ConfirmEmailPage";
import { FaqPage } from "@/pages/FaqPage";
import { ContactsPage } from "@/pages/ContactsPage";
import { PrivacyPolicyPage } from "@/pages/PrivacyPolicyPage";
import { TermsOfServicePage } from "@/pages/TermsOfServicePage";
import { AdminAnalyticsPage } from "@/pages/AdminAnalyticsPage";
import { AdminShopClicksPage } from "@/pages/AdminShopClicksPage";
import { AdminShopClickHistoryPage } from "@/pages/AdminShopClickHistoryPage";
import { AdminPageSnapshotsPage } from "@/pages/AdminPageSnapshotsPage";
import { AnalyticsTracker } from "@/hooks/useAnalyticsTracking";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 60 * 24,
      retry: 1,
    },
  },
});

const persister =
  typeof window !== "undefined"
    ? createSyncStoragePersister({
        storage: window.localStorage,
        key: "boardo-rq-cache-v1",
      })
    : undefined;

export default function App() {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: persister!,
        maxAge: 1000 * 60 * 60 * 24,
        buster: "v1",
        dehydrateOptions: {
          shouldDehydrateQuery: (query) =>
            query.state.status === "success" &&
            !query.queryKey.some(
              (k) => typeof k === "string" && k.startsWith("auth"),
            ),
        },
      }}
    >
      <AuthProvider>
        <BrowserRouter>
          <AnalyticsTracker />
          <Routes>
            {/* Public (no layout) */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/confirm-email" element={<ConfirmEmailPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />

            {/* Public with layout */}
            <Route path="/" element={<AppLayout />}>
              <Route index element={<HomePage />} />
              <Route path="games" element={<GamesPage />} />
              <Route path="games/:id" element={<GameDetailPage />} />
              <Route path="games/:id/kainos" element={<GamePriceHistoryPage />} />
              <Route path="listings" element={<ListingsPage />} />
              <Route path="listings/:id" element={<ListingDetailPage />} />
              <Route
                path="listing-groups/:groupId"
                element={<ListingGroupPage />}
              />
              <Route path="collections/:id" element={<CollectionPage />} />
              <Route path="search" element={<SearchRedirect />} />
              <Route path="zaidimai/:slug" element={<CategoryLandingRedirect />} />
              <Route path="users/:id" element={<UserProfilePage />} />
              <Route path="duk" element={<FaqPage />} />
              <Route path="kontaktai" element={<ContactsPage />} />
              <Route path="privatumo-politika" element={<PrivacyPolicyPage />} />
              <Route path="naudojimo-taisykles" element={<TermsOfServicePage />} />
            </Route>

            {/* Protected with layout */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route path="listings/new" element={<NewListingPage />} />
              <Route path="listings/:id/edit" element={<EditListingPage />} />
              <Route path="my-listings" element={<MyListingsPage />} />
              <Route path="my-collections" element={<MyCollectionsPage />} />
              <Route path="messages" element={<MessagesPage />} />
              <Route path="messages/:threadId" element={<MessageThreadPage />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="wishlists" element={<WishlistsPage />} />
              <Route path="profile" element={<EditProfilePage />} />
              <Route path="profile/change-email" element={<ChangeEmailPage />} />
              <Route path="profile/change-password" element={<ChangePasswordPage />} />
              <Route path="profile/notifications" element={<NotificationSettingsPage />} />
            </Route>

            {/* Admin with layout */}
            <Route
              path="/"
              element={
                <AdminRoute>
                  <AppLayout />
                </AdminRoute>
              }
            >
              <Route path="admin" element={<AdminPage />} />
              <Route path="admin/users" element={<AdminUsersPage />} />
              <Route path="admin/ai-requests" element={<AdminAiRequestsPage />} />
              <Route path="admin/error-logs" element={<AdminErrorLogsPage />} />
              <Route path="admin/jobs" element={<AdminJobsPage />} />
              <Route path="admin/listings" element={<AdminListingsPage />} />
              <Route path="admin/shop-listings" element={<AdminShopListingsPage />} />
              <Route path="admin/games" element={<AdminGamesPage />} />
              <Route path="admin/games/:id" element={<AdminGamePreviewPage />} />
              <Route path="admin/merge-games" element={<AdminMergeGamesPage />} />
              <Route path="admin/components" element={<AdminComponentsPage />} />
              <Route path="admin/og-preview" element={<AdminOgPreviewPage />} />
              <Route path="admin/analytics" element={<AdminAnalyticsPage />} />
              <Route path="admin/shop_clicks" element={<AdminShopClicksPage />} />
              <Route path="admin/shop_clicks/:id" element={<AdminShopClickHistoryPage />} />
              <Route path="admin/page-snapshots" element={<AdminPageSnapshotsPage />} />
            </Route>

            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </PersistQueryClientProvider>
  );
}
